import HoverTooltip from "../HoverTooltip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { ACTION_CHIP } from "./triageParts.jsx";
import { usd } from "./InvestigateParts.jsx";

// The investigation's line in the Investigate tab: start, or while it runs,
// what is at work, what it has spent, the chat and stop. The investigation
// itself happens in the coordinator's chat.

// What an investigator at work on a case is doing, as the case row says it.
export const WORKING_WORD = { dig: "Digging", double_check: "Double-checking", check: "Checking" };

// Spend as the controls show it: cents always, so it reads the same from
// the first investigator on.
const money = (v) => (Number(v) > 0 ? usd(v) : "$0.00");

// The reader's controls, beside the tab's counts. `reading`: the scope is
// still being read, so there is nothing to start on yet. `spentHere`: what
// investigating this scope has cost in this browser, every page together.
// `strip`: laid along a strip as the Triage tab's standards are (what it
// says on the left, a line's worth larger, its controls at the right end),
// with `idle` saying where things stand while nothing runs.
export function SwarmControls({ swarm, canCoordinate, reading = false, spentHere = 0, onStart, onStop, onTalk, strip = false, idle = null }) {
  const sofar = spentHere > 0 ? ` ${usd(spentHere)} spent on this investigation in this browser so far.` : "";
  const line = strip ? "text-[12px] leading-[1.3]" : "text-[11px] leading-[1.3]";
  const end = strip ? "ml-auto" : "";
  if (!swarm?.on) {
    return (
      <span data-investigate-swarm="off" className={`flex flex-wrap items-center gap-x-[10px] gap-y-[4px] ${strip ? "min-w-0 flex-1" : ""}`}>
        {strip && idle ? <span data-investigate-idle="" className={`${line} text-[color:var(--ink)]`}>{idle}</span> : null}
        {strip && swarm?.spentUsd > 0 ? <span className={NOTE_CLASS}>{usd(swarm.spentUsd)} spent since this page opened</span> : null}
        <HoverTooltip
          label={reading
            ? "Ready once the findings and what was checked have been read."
            : canCoordinate
              ? `Opens the investigation's chat. Pick what matters most to you there, or write it, and start it: it sends investigators to what can change a decision, shows them at work, and tells you what needs you. What they spend shows here, and Stop stops them.${sofar}`
              : `Sends investigators to what can change a decision: they read the sessions and what ran on the machines, and note what matters and what the first passes missed. What they spend shows here, and Stop stops them.${sofar}`}
          placement="top"
          wide
        >
          <button type="button" data-investigate-start="" disabled={reading} onClick={onStart} className={`${ACTION_CHIP} ${end} border-[color:var(--line-strong)] text-[color:var(--ink)] disabled:opacity-50`}>
            {swarm?.startedAt ? "Start again" : "Start investigating"}
          </button>
        </HoverTooltip>
        {!strip && swarm?.spentUsd > 0 ? <span className={NOTE_CLASS}>{usd(swarm.spentUsd)} spent since this page opened</span> : null}
      </span>
    );
  }
  const atWork = swarm.investigators.filter((i) => i.status === "running").length;
  const waiting = swarm.investigators.filter((i) => i.status === "queued").length;
  return (
    <span data-investigate-swarm="on" aria-live="polite" className={`flex flex-wrap items-center gap-x-[10px] gap-y-[4px] ${strip ? "min-w-0 flex-1" : ""}`}>
      <span aria-hidden="true" className={`h-[6px] w-[6px] shrink-0 rounded-full bg-[color:var(--ink)] ${atWork ? "motion-safe:animate-pulse" : ""}`} />
      <span className={`${line} text-[color:var(--ink)]`}>
        {atWork ? `${atWork} at work` : waiting ? "Starting" : "Investigating"}
        {waiting ? <span className="text-[color:var(--muted)]">{` \u00b7 ${waiting} waiting`}</span> : null}
      </span>
      <HoverTooltip
        label={`Investigators${swarm.coordinatorUsd > 0 ? " and the coordinator's own turns" : ""} have spent ${money(swarm.spentUsd)} since this page opened.${sofar}`}
        placement="top"
        wide
      >
        <span data-investigate-spend="" className="text-[11px] leading-[1.3] tabular-nums text-[color:var(--muted)]">{money(swarm.spentUsd)} spent</span>
      </HoverTooltip>
      {canCoordinate ? (
        <HoverTooltip label="Brings the investigation's chat on screen: ask it what it found, tell it what matters, or send it after something." placement="top" wide>
          <button type="button" data-investigate-talk="" onClick={onTalk} className={`${ACTION_CHIP} ${end}`}>Open the chat</button>
        </HoverTooltip>
      ) : null}
      <button type="button" data-investigate-stop="" onClick={onStop} className={`${ACTION_CHIP} ${canCoordinate ? "" : end}`}>Stop</button>
    </span>
  );
}
